import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pill, Check, X, Trash2, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = ['Daily', 'Twice daily', 'Three times daily', 'As needed', 'Weekly'];
const TIMES = ['Morning', 'Afternoon', 'Evening', 'Night', 'With food'];

function MedCard({ med, todayStatus, onTake, onDelete }) {
  const takenToday = todayStatus?.taken_today;
  
  return (
    <div className={`card-soft ${takenToday ? 'bg-green-50 border-green-200' : ''}`} data-testid={`med-${med.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-xl ${takenToday ? 'bg-green-100' : 'bg-secondary'}`}>
            <Pill className={`h-5 w-5 ${takenToday ? 'text-green-600' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <h3 className="font-medium text-foreground">{med.name}</h3>
            {med.dosage && <p className="text-sm text-muted-foreground">{med.dosage}</p>}
            <div className="flex items-center gap-2 mt-1">
              {med.frequency && (
                <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">{med.frequency}</span>
              )}
              {med.time_of_day?.length > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {med.time_of_day.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          {!takenToday && (
            <button
              onClick={() => onTake(med.id)}
              className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200"
              title="Mark as taken"
              data-testid="take-med-btn"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onDelete(med.id)}
            className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full"
            title="Delete"
            data-testid="delete-med-btn"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {takenToday && (
        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
          <Check className="h-3 w-3" /> Taken today
        </p>
      )}
    </div>
  );
}

export default function Medications() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [medications, setMedications] = useState([]);
  const [todayStatus, setTodayStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('');
  const [times, setTimes] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [medsRes, statusRes] = await Promise.all([
        axios.get(`${API}/medications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/medications/today`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMedications(medsRes.data);
      setTodayStatus(statusRes.data);
    } catch (err) {
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async () => {
    if (!name.trim()) {
      toast.error('Please enter medication name');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/medications`, {
        name,
        dosage: dosage || null,
        frequency: frequency || null,
        time_of_day: times
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Medication added');
      setShowAdd(false);
      setName('');
      setDosage('');
      setFrequency('');
      setTimes([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const handleTake = async (medId) => {
    try {
      await axios.post(`${API}/medications/log`, {
        medication_id: medId,
        taken: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Marked as taken!');
      fetchData();
    } catch (err) {
      toast.error('Failed to log');
    }
  };

  const handleDelete = async (medId) => {
    if (!window.confirm('Delete this medication?')) return;
    try {
      await axios.delete(`${API}/medications/${medId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleTime = (time) => {
    if (times.includes(time)) {
      setTimes(times.filter(t => t !== time));
    } else {
      setTimes([...times, time]);
    }
  };

  const takenCount = todayStatus.filter(m => m.taken_today).length;
  const totalCount = todayStatus.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-safe" data-testid="medications-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" /><span>Back</span>
        </button>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Medications</h1>
          <p className="mt-2 text-muted-foreground">Track your daily medications</p>
        </div>

        {/* Today's Progress */}
        {totalCount > 0 && (
          <div className="mt-6 card-soft bg-gradient-to-br from-blue-50 to-green-50 border-blue-200/50">
            <p className="text-sm text-muted-foreground">Today's Progress</p>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${(takenCount / totalCount) * 100}%` }}
                />
              </div>
              <span className="font-medium text-foreground">{takenCount}/{totalCount}</span>
            </div>
          </div>
        )}

        {/* Add Button */}
        {!showAdd && (
          <Button onClick={() => setShowAdd(true)} className="mt-6 w-full btn-secondary" data-testid="add-med-btn">
            <Plus className="h-5 w-5 mr-2" />Add Medication
          </Button>
        )}

        {/* Add Form */}
        {showAdd && (
          <div className="mt-6 card-soft page-enter" data-testid="add-med-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground">Add Medication</h2>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <Input
              placeholder="Medication name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mb-3"
              data-testid="med-name-input"
            />
            
            <Input
              placeholder="Dosage (e.g., 400mg)"
              value={dosage}
              onChange={(e) => setDosage(e.target.value)}
              className="mb-3"
              data-testid="med-dosage-input"
            />
            
            <div className="mb-3">
              <p className="text-sm text-muted-foreground mb-2">Frequency</p>
              <div className="flex flex-wrap gap-2">
                {FREQUENCIES.map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFrequency(frequency === f ? '' : f)}
                    className={`px-3 py-1 text-sm rounded-full ${
                      frequency === f ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Time of day</p>
              <div className="flex flex-wrap gap-2">
                {TIMES.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTime(t)}
                    className={`px-3 py-1 text-sm rounded-full ${
                      times.includes(t) ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            
            <Button onClick={handleAdd} disabled={saving} className="w-full btn-primary" data-testid="save-med-btn">
              {saving ? 'Adding...' : 'Add Medication'}
            </Button>
          </div>
        )}

        {/* Medications List */}
        <div className="mt-6 space-y-3" data-testid="medications-list">
          {medications.length === 0 ? (
            <div className="card-soft text-center py-8">
              <Pill className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No medications added yet</p>
              <p className="text-sm text-muted-foreground mt-1">Add your medications to track them daily</p>
            </div>
          ) : (
            medications.map(med => {
              const status = todayStatus.find(s => s.id === med.id);
              return (
                <MedCard 
                  key={med.id} 
                  med={med} 
                  todayStatus={status}
                  onTake={handleTake}
                  onDelete={handleDelete}
                />
              );
            })
          )}
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
